import { Inject, Injectable, InjectionToken } from '@angular/core';
import { ResolveEnd, Router } from '@angular/router';
import { Platform } from '@angular/cdk/platform';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as _ from 'lodash';

export const FUSE_CONFIG = new InjectionToken<any>('fuseCustomConfig');

@Injectable({
    providedIn: 'root'
})
export class FuseConfigService {

    // Private
    private _configSubject: BehaviorSubject<any>;
    private readonly _defaultConfig: any;

    /**
     * Constructor
     */
    constructor(
        private _platform: Platform,
        private _router: Router,
        @Inject(FUSE_CONFIG) private _config: any
    ) {
        // Set the default config
        this._defaultConfig = _.cloneDeep(_config);

        // Initialize the service
        this._init();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set config
     */
    set config(value: any) {
        let config = this._configSubject.getValue();

        // Merge the new config
        config = _.merge({}, config, value);

        // Notify observers
        this._configSubject.next(config);
    }

    /**
     * Get config
     */
    get config(): Observable<any> {
        return this._configSubject.asObservable();
    }

    /**
     * Get default config
     */
    get defaultConfig(): any {
        return this._defaultConfig;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Initialize
     */
    private _init(): void {

        /**
         * Disable custom scrollbars on mobile
         */
        if (this._platform.ANDROID || this._platform.IOS) {
            this._defaultConfig.customScrollbars = false;
        }

        // Initialize config
        this._configSubject = new BehaviorSubject<any>(
            _.cloneDeep(this._defaultConfig)
        );

        /**
         * Reset layout config on every ResolveEnd event
         */
        this._router.events
            .pipe(
                filter((event): event is ResolveEnd => event instanceof ResolveEnd)
            )
            .subscribe(() => {

                const currentConfig = this._configSubject.getValue();

                if (
                    !_.isEqual(
                        currentConfig.layout,
                        this._defaultConfig.layout
                    )
                ) {
                    const config = _.cloneDeep(currentConfig);

                    config.layout = _.cloneDeep(
                        this._defaultConfig.layout
                    );

                    this._configSubject.next(config);
                }
            });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set config
     */
    setConfig(
        value: any,
        opts: { emitEvent: boolean } = { emitEvent: true }
    ): void {

        let config = this._configSubject.getValue();

        // Merge config
        config = _.merge({}, config, value);

        // Emit changes
        if (opts.emitEvent === true) {
            this._configSubject.next(config);
        }
    }

    /**
     * Get config
     */
    getConfig(): Observable<any> {
        return this._configSubject.asObservable();
    }

    /**
     * Reset config to defaults
     */
    resetToDefaults(): void {
        this._configSubject.next(
            _.cloneDeep(this._defaultConfig)
        );
    }
}